import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { ArrowSquareOut, ChatCircleDots } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GRADE_CLS = { strong: "bg-emerald-700", moderate: "bg-blue-700", limited: "bg-amber-500", missing: "bg-red-600" };

const Answer = ({ q, a, testId }) => (
  <div className="bg-white border border-slate-200 p-4" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-widest text-slate-400">{q}</p>
    <p className="text-xs text-slate-700 mt-1.5 leading-relaxed">{a}</p>
  </div>
);

export default function EvidenceInsightsPage() {
  const { productId, product } = useProduct();
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    axios.get(`${API}/evidence-experience/${productId}/insights`).then((r) => setD(r.data)).catch(console.error);
  }, [productId]);

  return (
    <PageShell pageId="evidence-insights" overline={`Evidence · ${product?.name || ""}`} title="Evidence Insights"
      actions={
        <div className="flex gap-2">
          <Link to="/console/gaps" data-testid="open-gap-analysis" className="border border-slate-300 text-slate-700 text-xs font-semibold px-3 py-2 hover:bg-slate-50 flex items-center gap-1.5"><ArrowSquareOut size={12} /> Gap Analysis</Link>
          <Link to="/console/reusability" data-testid="open-reuse-engine" className="border border-slate-300 text-slate-700 text-xs font-semibold px-3 py-2 hover:bg-slate-50 flex items-center gap-1.5"><ArrowSquareOut size={12} /> Reuse Engine</Link>
          <Link to="/console" data-testid="view-readiness" className="bg-slate-900 text-white text-xs font-semibold px-3 py-2 hover:bg-slate-800 flex items-center gap-1.5">View Global Readiness</Link>
        </div>
      }>
      {!d ? <p className="px-4 py-10 text-center text-xs text-slate-400">Loading…</p> : (
        <>
          <div className="grid md:grid-cols-2 gap-4" data-testid="insight-answers">
            <Answer testId="answer-have" q="What evidence do we have?" a={d.answers.what_we_have} />
            <Answer testId="answer-strong" q="How strong is it?" a={d.answers.how_strong} />
            <Answer testId="answer-claims" q="What can we confidently claim?" a={d.answers.confident_claims} />
            <Answer testId="answer-missing" q="What evidence is missing?" a={d.answers.what_is_missing} />
            <Answer testId="answer-reuse" q="Which evidence is reusable?" a={d.answers.reuse} />
            <Answer testId="answer-localization" q="Where is localization necessary?" a={d.answers.localization} />
          </div>

          <div className="bg-white border border-slate-200 p-5" data-testid="domain-strength">
            <p className="font-heading font-bold text-sm mb-4">Evidence strength by domain</p>
            <div className="space-y-2.5">
              {d.domains.map((dm) => (
                <div key={dm.id} className="flex items-center gap-3" data-testid={`domain-${dm.id}`}>
                  <p className="w-44 shrink-0 text-xs font-semibold text-slate-700 text-right">{dm.label}</p>
                  <div className="flex-1 h-4 bg-slate-100 relative">
                    <div className={`h-full ${GRADE_CLS[dm.grade]}`} style={{ width: `${Math.max(dm.strength, 2)}%` }} />
                  </div>
                  <p className="w-32 shrink-0 text-[10px] text-slate-500">
                    <b className="uppercase">{dm.grade}</b> · {dm.verified}/{dm.assets} verified
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 p-5" data-testid="markets-supported">
              <p className="font-heading font-bold text-sm mb-3">Which markets are currently supported?</p>
              {d.markets.supported.length === 0 && d.markets.partial.length === 0
                ? <p className="text-xs text-slate-400">No markets currently reach supported readiness.</p> : (
                <>
                  {d.markets.supported.map((m) => (
                    <p key={m.id} className="text-xs text-slate-700 flex justify-between py-1 border-b border-slate-50">
                      <span>{m.name}</span><span className="font-mono-data font-bold text-emerald-700">{m.readiness}</span></p>
                  ))}
                  {d.markets.partial.map((m) => (
                    <p key={m.id} className="text-xs text-slate-500 flex justify-between py-1 border-b border-slate-50">
                      <span>{m.name} (partial)</span><span className="font-mono-data font-bold text-amber-600">{m.readiness}</span></p>
                  ))}
                </>
              )}
              <p className="text-[10px] text-slate-400 mt-2">{d.markets.total} markets tracked · readiness ≥60 = supported</p>
            </div>
            <div className="bg-white border border-slate-200 p-5" data-testid="investments">
              <p className="font-heading font-bold text-sm mb-3">Evidence investments that improve readiness fastest</p>
              {d.investments.length === 0 ? <p className="text-xs text-slate-400">All domains show solid coverage.</p>
                : d.investments.map((iv, i) => (
                <div key={iv.domain} className="flex gap-3 py-2 border-b border-slate-50 last:border-0">
                  <span className="w-6 h-6 shrink-0 flex items-center justify-center bg-slate-900 text-white font-mono-data text-xs font-bold">{i + 1}</span>
                  <div><p className="text-xs font-bold text-slate-800">{iv.domain}</p>
                    <p className="text-[11px] text-slate-500">{iv.action}</p></div>
                </div>
              ))}
              <button data-testid="ask-mai-insights" onClick={() => document.querySelector('[data-testid="global-ask-mai-btn"]')?.click()}
                className="mt-3 text-xs font-semibold text-slate-700 border border-slate-300 px-3 py-2 hover:bg-slate-50 flex items-center gap-1.5">
                <ChatCircleDots size={13} /> Ask MAI about this evidence base
              </button>
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
}
